import { CORE_URL } from "@/constants/api";
import { auth } from "@/services/auth-service";
import {
	getSelfOrganizationForms,
	selfOrganizationEquipmentAreas,
} from "@/services/organization-service";
import { selfUpdateUserComprehensive } from "@/services/user-service";
import {
	type Manufacturer,
	getManufacturers,
	getOrganizationAssetTypes,
} from "@/services/utility-service";
import { UserAccessLevel, useUserStore } from "@/stores/user-store";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import {
	Button,
	Card,
	Image,
	Space,
	Switch,
	Table,
	Tag,
	Typography,
	Upload,
	message,
} from "antd";
import type { RcFile } from "antd/es/upload/interface";
import {
	deleteObject,
	getDownloadURL,
	getStorage,
	ref,
	uploadBytes,
} from "firebase/storage";
import type { FC } from "react";
import { useCallback, useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AssetTypeModal } from "./AssetTypeModal";
import { type CustomField, CustomFieldsModal } from "./CustomFieldsModal";
import { EquipmentAreaModal } from "./EquipmentAreaModal";
import { ManufacturerModal } from "./ManufacturerModal";

const VITE_CORE_API_URL = import.meta.env.VITE_CORE_API_URL;

const { Title, Text } = Typography;

interface SettingsState {
	autoSaveDrafts: boolean;
	useCameraOnPhotoCapture: boolean;
	isLoading: boolean;
	assetTypes: any[];
	assetTypesLoading: boolean;
	showAssetTypeModal: boolean;
	manufacturers: Manufacturer[];
	manufacturersLoading: boolean;
	showManufacturerModal: boolean;
	showCustomFieldsModal: boolean;
	equipmentAreas: any[];
	equipmentAreasLoading: boolean;
	showEquipmentAreaModal: boolean;
	logoUrl: string | null;
	logoUploading: boolean;
}

type SettingsAction =
	| { type: "SET_AUTO_SAVE"; payload: boolean }
	| { type: "SET_CAMERA_USAGE"; payload: boolean }
	| { type: "SET_LOADING"; payload: boolean }
	| { type: "SET_ASSET_TYPES"; payload: any[] }
	| { type: "SET_ASSET_TYPES_LOADING"; payload: boolean }
	| { type: "SET_SHOW_ASSET_TYPE_MODAL"; payload: boolean }
	| { type: "SET_MANUFACTURERS"; payload: Manufacturer[] }
	| { type: "SET_MANUFACTURERS_LOADING"; payload: boolean }
	| { type: "SET_SHOW_MANUFACTURER_MODAL"; payload: boolean }
	| { type: "SET_SHOW_CUSTOM_FIELDS_MODAL"; payload: boolean }
	| { type: "SET_EQUIPMENT_AREAS"; payload: any[] }
	| { type: "SET_EQUIPMENT_AREAS_LOADING"; payload: boolean }
	| { type: "SET_SHOW_EQUIPMENT_AREA_MODAL"; payload: boolean }
	| { type: "SET_LOGO_URL"; payload: string | null }
	| { type: "SET_LOGO_UPLOADING"; payload: boolean }
	| {
			type: "SYNC_WITH_USER";
			payload: {
				autoSave?: boolean;
				cameraUsage?: boolean;
				logoUrl?: string | null;
			};
	  };

const initialState: SettingsState = {
	autoSaveDrafts: true,
	useCameraOnPhotoCapture: false,
	isLoading: false,
	assetTypes: [],
	assetTypesLoading: false,
	showAssetTypeModal: false,
	manufacturers: [],
	manufacturersLoading: false,
	showManufacturerModal: false,
	showCustomFieldsModal: false,
	equipmentAreas: [],
	equipmentAreasLoading: false,
	showEquipmentAreaModal: false,
	logoUrl: null,
	logoUploading: false,
};

const settingsReducer = (
	state: SettingsState,
	action: SettingsAction,
): SettingsState => {
	switch (action.type) {
		case "SET_AUTO_SAVE":
			return {
				...state,
				autoSaveDrafts: action.payload,
			};
		case "SET_CAMERA_USAGE":
			return {
				...state,
				useCameraOnPhotoCapture: action.payload,
			};
		case "SET_LOADING":
			return {
				...state,
				isLoading: action.payload,
			};
		case "SET_ASSET_TYPES":
			return {
				...state,
				assetTypes: action.payload,
			};
		case "SET_ASSET_TYPES_LOADING":
			return {
				...state,
				assetTypesLoading: action.payload,
			};
		case "SET_SHOW_ASSET_TYPE_MODAL":
			return {
				...state,
				showAssetTypeModal: action.payload,
			};
		case "SET_MANUFACTURERS":
			return {
				...state,
				manufacturers: action.payload,
			};
		case "SET_MANUFACTURERS_LOADING":
			return {
				...state,
				manufacturersLoading: action.payload,
			};
		case "SET_SHOW_MANUFACTURER_MODAL":
			return {
				...state,
				showManufacturerModal: action.payload,
			};
		case "SET_SHOW_CUSTOM_FIELDS_MODAL":
			return {
				...state,
				showCustomFieldsModal: action.payload,
			};
		case "SET_EQUIPMENT_AREAS":
			return {
				...state,
				equipmentAreas: action.payload,
			};
		case "SET_EQUIPMENT_AREAS_LOADING":
			return {
				...state,
				equipmentAreasLoading: action.payload,
			};
		case "SET_SHOW_EQUIPMENT_AREA_MODAL":
			return {
				...state,
				showEquipmentAreaModal: action.payload,
			};
		case "SET_LOGO_URL":
			return {
				...state,
				logoUrl: action.payload,
			};
		case "SET_LOGO_UPLOADING":
			return {
				...state,
				logoUploading: action.payload,
			};
		case "SYNC_WITH_USER":
			return {
				...state,
				autoSaveDrafts: action.payload.autoSave ?? state.autoSaveDrafts,
				useCameraOnPhotoCapture:
					action.payload.cameraUsage ?? state.useCameraOnPhotoCapture,
				logoUrl: action.payload.logoUrl ?? state.logoUrl,
			};
		default:
			return state;
	}
};

export const SettingsTab: FC = () => {
	const navigate = useNavigate();
	const { user, setUser } = useUserStore();
	const [state, dispatch] = useReducer(settingsReducer, initialState);
	const [assetTypePageSize, setAssetTypePageSize] = useState(5);
	const [manufacturerPageSize, setManufacturerPageSize] = useState(5);
	const [equipmentAreaPageSize, setEquipmentAreaPageSize] = useState(5);

	useEffect(() => {
		if (user) {
			dispatch({
				type: "SYNC_WITH_USER",
				payload: {
					autoSave: user.organization?.config?.auto_save_drafts ?? true,
					cameraUsage: user.user_config?.use_camera_on_photo_capture,
					logoUrl:
						user.organization?.config?.logoUrl ??
						user.organization?.config?.logo_url ??
						null,
				},
			});
		}
	}, [user]);

	const handleAutoSaveChange = useCallback(
		async (checked: boolean) => {
			if (!user?.organization) return;

			const previousUser = { ...user };
			const updatedUser = {
				...user,
				organization: {
					...user.organization,
					config: {
						...user.organization.config,
						auto_save_drafts: checked,
					},
				},
			};

			try {
				dispatch({ type: "SET_LOADING", payload: true });

				// Optimistic update
				setUser(updatedUser);
				dispatch({ type: "SET_AUTO_SAVE", payload: checked });

				// Sync with backend
				await selfUpdateUserComprehensive(updatedUser);
				message.success("Auto-save setting updated successfully");
			} catch (error: any) {
				// Rollback on failure
				setUser(previousUser);
				dispatch({ type: "SET_AUTO_SAVE", payload: !checked });
				message.error(error.message || "Failed to update auto-save setting");
			} finally {
				dispatch({ type: "SET_LOADING", payload: false });
			}
		},
		[user, setUser],
	);

	const handleCameraChange = useCallback(
		async (checked: boolean) => {
			if (!user) return;

			const previousUser = { ...user };
			const updatedUser = {
				...user,
				user_config: {
					...user.user_config,
					use_camera_on_photo_capture: checked,
				},
			};

			try {
				dispatch({ type: "SET_LOADING", payload: true });

				// Optimistic update
				setUser(updatedUser);
				dispatch({ type: "SET_CAMERA_USAGE", payload: checked });

				// Sync with backend
				const result = await selfUpdateUserComprehensive(updatedUser);
				message.success("Camera setting updated successfully");
			} catch (error: any) {
				// Rollback on failure
				setUser(previousUser);
				dispatch({ type: "SET_CAMERA_USAGE", payload: !checked });
				message.error(error.message || "Failed to update camera setting");
			} finally {
				dispatch({ type: "SET_LOADING", payload: false });
			}
		},
		[user, setUser],
	);

	const fetchAssetTypes = useCallback(async () => {
		if (!user) {
			return;
		}

		try {
			dispatch({ type: "SET_ASSET_TYPES_LOADING", payload: true });
			// Fetch all asset types including deprecated ones for management in settings
			const assetTypes = await getOrganizationAssetTypes(true);
			dispatch({ type: "SET_ASSET_TYPES", payload: assetTypes });
		} catch (error: any) {
			message.error(error.message || "Failed to fetch asset types");
		} finally {
			dispatch({ type: "SET_ASSET_TYPES_LOADING", payload: false });
		}
	}, [user]);

	const fetchManufacturers = useCallback(async () => {
		const userAccessLevel =
			user?.role?.accessLevel ?? user?.role?.access_level ?? 0;
		if (!user || !user.role || userAccessLevel < UserAccessLevel.SUPER_ADMIN) {
			return;
		}

		try {
			dispatch({ type: "SET_MANUFACTURERS_LOADING", payload: true });
			const manufacturers = await getManufacturers();
			dispatch({ type: "SET_MANUFACTURERS", payload: manufacturers });
		} catch (error: any) {
			message.error(error.message || "Failed to fetch manufacturers");
		} finally {
			dispatch({ type: "SET_MANUFACTURERS_LOADING", payload: false });
		}
	}, [user]);

	const fetchEquipmentAreas = useCallback(async () => {
		if (!user) {
			return;
		}

		try {
			dispatch({ type: "SET_EQUIPMENT_AREAS_LOADING", payload: true });
			const equipmentAreas = await selfOrganizationEquipmentAreas();
			dispatch({ type: "SET_EQUIPMENT_AREAS", payload: equipmentAreas });
		} catch (error: any) {
			message.error(error.message || "Failed to fetch equipment areas");
		} finally {
			dispatch({ type: "SET_EQUIPMENT_AREAS_LOADING", payload: false });
		}
	}, [user]);

	useEffect(() => {
		fetchAssetTypes();
		fetchManufacturers();
		fetchEquipmentAreas();
	}, [
		fetchAssetTypes,
		fetchManufacturers,
		fetchEquipmentAreas,
	]);

	const handleAssetTypeSuccess = useCallback(() => {
		fetchAssetTypes();
	}, [fetchAssetTypes]);

	const handleCustomFieldsSuccess = useCallback(async () => {
		// Refresh organization forms to get updated form configs
		// IMPORTANT: Only fetch forms, NOT the entire organization, to avoid accidentally switching organizations
		try {
			// Only fetch the forms - preserve the user's current organization data
			const updatedForms = await getSelfOrganizationForms();

			console.log("Fetched updated forms:", updatedForms);

			if (user && updatedForms) {
				// Update ONLY the forms in the user's organization, preserving everything else
				const updatedUser = {
					...user,
					organization: {
						...user.organization, // Preserve all existing organization data (id, name, config, etc.)
						forms: updatedForms, // Only update the forms array
					},
				};
				console.log(
					"Updated user organization forms (preserved org ID:",
					updatedUser.organization.id,
					")",
				);
				setUser(updatedUser);
			}
		} catch (error: any) {
			console.error("Failed to refresh organization forms:", error);
		}
	}, [user, setUser]);

	const handleManufacturerSuccess = useCallback(() => {
		fetchManufacturers();
	}, [fetchManufacturers]);

	const handleEquipmentAreaSuccess = useCallback(() => {
		fetchEquipmentAreas();
	}, [fetchEquipmentAreas]);

	const handleLogoUpload = useCallback(
		async (file: RcFile): Promise<boolean> => {
			if (!user?.organization?.id) {
				message.error("Organization not found");
				return false;
			}

			try {
				dispatch({ type: "SET_LOGO_UPLOADING", payload: true });

				// Validate file type
				const isImage = file.type.startsWith("image/");
				if (!isImage) {
					message.error("You can only upload image files!");
					return false;
				}

				// Validate file size (5MB)
				const isLt5M = file.size / 1024 / 1024 < 5;
				if (!isLt5M) {
					message.error("Image must be smaller than 5MB!");
					return false;
				}

				// Upload to Firebase Storage
				const storage = getStorage();
				const fileExtension = file.name.split(".").pop();
				const fileName = `org-${user.organization.id}-logo.${fileExtension}`;
				const storageRef = ref(storage, `organization-logos/${fileName}`);

				await uploadBytes(storageRef, file);
				const downloadURL = await getDownloadURL(storageRef);

				// Update organization config via API
				const response = await fetch(
					`${VITE_CORE_API_URL}${CORE_URL}/organization/${user.organization.id}/logo`,
					{
						method: "PUT",
						headers: {
							Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
							"Content-Type": "application/json",
						},
						body: JSON.stringify({ logoUrl: downloadURL }),
					},
				);

				if (!response.ok) {
					throw new Error("Failed to update logo");
				}

				// Update local state
				dispatch({ type: "SET_LOGO_URL", payload: downloadURL });

				// Update user store
				const updatedUser = {
					...user,
					organization: {
						...user.organization,
						config: {
							...user.organization.config,
							logoUrl: downloadURL,
							logo_url: downloadURL, // Keep both for compatibility
						},
					},
				};
				setUser(updatedUser);

				message.success("Logo uploaded successfully!");
				return false; // Prevent antd upload from doing its own upload
			} catch (error: any) {
				console.error("Error uploading logo:", error);
				message.error(error.message || "Failed to upload logo");
				return false;
			} finally {
				dispatch({ type: "SET_LOGO_UPLOADING", payload: false });
			}
		},
		[user, setUser],
	);

	const handleLogoDelete = useCallback(async () => {
		if (!user?.organization?.id) {
			message.error("Organization not found");
			return;
		}

		if (!state.logoUrl) {
			return;
		}

		try {
			dispatch({ type: "SET_LOGO_UPLOADING", payload: true });

			// Delete from Firebase Storage
			try {
				const storage = getStorage();
				const logoRef = ref(storage, state.logoUrl);
				await deleteObject(logoRef);
			} catch (storageError) {
				// Continue even if storage delete fails (file might not exist)
				console.warn("Failed to delete from storage:", storageError);
			}

			// Update organization config via API
			const response = await fetch(
				`${VITE_CORE_API_URL}${CORE_URL}/organization/${user.organization.id}/logo`,
				{
					method: "DELETE",
					headers: {
						Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
					},
				},
			);

			if (!response.ok) {
				throw new Error("Failed to delete logo");
			}

			// Update local state
			dispatch({ type: "SET_LOGO_URL", payload: null });

			// Update user store
			const updatedUser = {
				...user,
				organization: {
					...user.organization,
					config: {
						...user.organization.config,
						logoUrl: null,
						logo_url: null, // Keep both for compatibility
					},
				},
			};
			setUser(updatedUser);

			message.success("Logo removed successfully!");
		} catch (error: any) {
			console.error("Error deleting logo:", error);
			message.error(error.message || "Failed to remove logo");
		} finally {
			dispatch({ type: "SET_LOGO_UPLOADING", payload: false });
		}
	}, [user, setUser, state.logoUrl]);

	// Use camelCase accessLevel (from backend) with fallback to snake_case access_level
	const userAccessLevel =
		user?.role?.accessLevel ?? user?.role?.access_level ?? 0;
	const isSuperAdmin = userAccessLevel >= UserAccessLevel.SUPER_ADMIN;
	const isAdmin = userAccessLevel >= UserAccessLevel.ADMIN;

	const assetTypeColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Master Asset Type ID",
			dataIndex: "masterAssetTypeId",
			key: "masterAssetTypeId",
		},
		{
			title: "Created",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (value: string) => {
				if (!value) return "-";
				try {
					return new Date(value).toLocaleDateString();
				} catch {
					return value;
				}
			},
		},
	];

	const manufacturerColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Verified",
			dataIndex: "isVerified",
			key: "isVerified",
			render: (isVerified: boolean) => (
				<Tag color={isVerified ? "green" : "default"}>
					{isVerified ? "Verified" : "Not Verified"}
				</Tag>
			),
		},
		{
			title: "Date Added",
			dataIndex: "timeAdded",
			key: "timeAdded",
			render: (value: string) => {
				if (!value) return "-";
				try {
					return new Date(value).toLocaleDateString();
				} catch {
					return value;
				}
			},
		},
	];

	const equipmentAreaColumns = [
		{
			title: "Name",
			dataIndex: "name",
			key: "name",
		},
		{
			title: "Status",
			key: "status",
			render: (_: any, record: any) => (
				<Tag color={record.isDeprecated ? "red" : "green"}>
					{record.isDeprecated ? "Deprecated" : "Active"}
				</Tag>
			),
		},
		{
			title: "Created",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (value: string) => {
				if (!value) return "-";
				try {
					return new Date(value).toLocaleDateString();
				} catch {
					return value;
				}
			},
		},
	];

	if (!user) {
		return null;
	}

	return (
		<div className="max-w-6xl mx-auto p-6">
			<Title level={4} className="mb-2">
				Settings
			</Title>
			<Text className="text-gray-500 block mb-6">
				Manage your application settings here.
			</Text>

			<div className="space-y-6">
				<Card className="shadow-sm">
					<div className="space-y-6">
						<div className="flex justify-between items-center">
							<div>
								<Text strong className="block">
									Auto Save Drafts
								</Text>
								<Text className="text-gray-500">
									Automatically save your work as drafts
								</Text>
							</div>
							<Switch
								checked={state.autoSaveDrafts}
								onChange={handleAutoSaveChange}
								disabled={state.isLoading}
							/>
						</div>

						<div className="flex justify-between items-center">
							<div>
								<Text strong className="block">
									Use Camera on Photo Capture
								</Text>
								<Text className="text-gray-500">
									Enable camera access for photo capture
								</Text>
							</div>
							<Switch
								checked={state.useCameraOnPhotoCapture}
								onChange={handleCameraChange}
								disabled={state.isLoading}
							/>
						</div>

						<div className="pt-4 border-t">
							<Text
								className="text-blue-600 cursor-pointer hover:underline"
								onClick={() => navigate("/releases")}
							>
								View Releases
							</Text>
						</div>
					</div>
				</Card>

				{/* Company Logo - Available to all admins */}
				{isAdmin && (
					<Card className="shadow-sm">
						<div className="space-y-4">
							<div>
								<Title level={5} className="mb-1">
									Company Logo
								</Title>
								<Text className="text-gray-500">
									Upload or change your organization's logo
								</Text>
							</div>

							{state.logoUrl ? (
								<div className="space-y-4">
									<div className="flex items-center justify-center p-4 border border-gray-200 rounded-lg bg-gray-50">
										<Image
											src={state.logoUrl}
											alt="Company Logo"
											style={{
												maxHeight: 200,
												maxWidth: "100%",
												objectFit: "contain",
											}}
											preview={true}
										/>
									</div>
									<Space>
										<Upload
											beforeUpload={handleLogoUpload}
											showUploadList={false}
											accept="image/*"
											disabled={state.logoUploading}
										>
											<Button
												icon={<UploadOutlined />}
												loading={state.logoUploading}
											>
												Change Logo
											</Button>
										</Upload>
										<Button
											icon={<DeleteOutlined />}
											danger
											onClick={handleLogoDelete}
											loading={state.logoUploading}
										>
											Remove Logo
										</Button>
									</Space>
								</div>
							) : (
								<div className="space-y-4">
									<div className="flex items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
										<Text className="text-gray-400">No logo uploaded</Text>
									</div>
									<Upload
										beforeUpload={handleLogoUpload}
										showUploadList={false}
										accept="image/*"
										disabled={state.logoUploading}
									>
										<Button
											type="primary"
											icon={<UploadOutlined />}
											loading={state.logoUploading}
										>
											Upload Logo
										</Button>
									</Upload>
								</div>
							)}
						</div>
					</Card>
				)}

				{/* Asset Types - Available to all users */}
				<Card className="shadow-sm">
					<div className="space-y-4">
						<div className="flex justify-between items-center">
							<div>
								<Title level={5} className="mb-1">
									Organization Asset Types
								</Title>
								<Text className="text-gray-500">
									Manage asset types for your organization
								</Text>
							</div>
							<Button
								type="primary"
								onClick={() =>
									dispatch({ type: "SET_SHOW_ASSET_TYPE_MODAL", payload: true })
								}
							>
								Edit Asset Types
							</Button>
						</div>

						<Table
							columns={assetTypeColumns}
							dataSource={state.assetTypes}
							loading={state.assetTypesLoading}
							rowKey="id"
							pagination={{
								pageSize: assetTypePageSize,
								showSizeChanger: true,
								pageSizeOptions: ["5", "10", "20", "50"],
								onShowSizeChange: (current, size) => {
									setAssetTypePageSize(size);
								},
								showQuickJumper: true,
								showTotal: (total, range) =>
									`${range[0]}-${range[1]} of ${total} asset types`,
							}}
							scroll={{ x: 800 }}
						/>
					</div>
				</Card>

				{/* Equipment Areas - Available to all users */}
				<Card className="shadow-sm">
					<div className="space-y-4">
						<div className="flex justify-between items-center">
							<div>
								<Title level={5} className="mb-1">
									Equipment Areas (Asset Locations)
								</Title>
								<Text className="text-gray-500">
									Manage equipment areas (asset locations) for your organization
								</Text>
							</div>
							<Button
								type="primary"
								onClick={() =>
									dispatch({
										type: "SET_SHOW_EQUIPMENT_AREA_MODAL",
										payload: true,
									})
								}
							>
								Manage Equipment Areas
							</Button>
						</div>

						<Table
							columns={equipmentAreaColumns}
							dataSource={state.equipmentAreas}
							loading={state.equipmentAreasLoading}
							rowKey="id"
							pagination={{
								pageSize: equipmentAreaPageSize,
								showSizeChanger: true,
								pageSizeOptions: ["5", "10", "20", "50"],
								onShowSizeChange: (current, size) => {
									setEquipmentAreaPageSize(size);
								},
								showQuickJumper: true,
								showTotal: (total, range) =>
									`${range[0]}-${range[1]} of ${total} equipment areas`,
							}}
							scroll={{ x: 600 }}
						/>
					</div>
				</Card>

				{/* Custom Fields - Available to all users */}
				<Card className="shadow-sm">
					<div className="space-y-4">
						<div className="flex justify-between items-center">
							<div>
								<Title level={5} className="mb-1">
									Custom Form Fields
								</Title>
								<Text className="text-gray-500">
									Add custom fields that appear at the end of the submission
									form. These fields are stored in the asset's uniqueFields
									property.
								</Text>
							</div>
							<Button
								type="primary"
								onClick={() =>
									dispatch({
										type: "SET_SHOW_CUSTOM_FIELDS_MODAL",
										payload: true,
									})
								}
							>
								Manage Custom Fields
							</Button>
						</div>

						{(() => {
							// Find form_id 3 and get its config
							const formId3 = user?.organization?.forms?.find(
								(f: any) => f.formId === 3 || f.form_id === 3,
							);
							// For form_id 3, config is the array directly; otherwise check config.uniqueFields
							let uniqueFields = null;
							if (formId3?.config) {
								if (Array.isArray(formId3.config)) {
									uniqueFields = formId3.config;
								} else if (formId3.config.uniqueFields) {
									uniqueFields = formId3.config.uniqueFields;
								}
							}
							const hasFields =
								Array.isArray(uniqueFields) && uniqueFields.length > 0;

							return hasFields ? (
								<div className="mt-4">
									<Text className="text-gray-600">
										<strong>{uniqueFields.length}</strong> custom field(s)
										configured
									</Text>
								</div>
							) : (
								<div className="mt-4">
									<Text className="text-gray-400 italic">
										No custom fields configured. Click "Manage Custom Fields" to
										add some.
									</Text>
								</div>
							);
						})()}
					</div>
				</Card>

				{/* Super Admin Only Sections */}
				{isSuperAdmin && (
					<>
						<Card className="shadow-sm">
							<div className="space-y-4">
								<div className="flex justify-between items-center">
									<div>
										<Title level={5} className="mb-1">
											Manufacturers
										</Title>
										<Text className="text-gray-500">
											Manage manufacturers for your organization
										</Text>
									</div>
									<Button
										type="primary"
										onClick={() =>
											dispatch({
												type: "SET_SHOW_MANUFACTURER_MODAL",
												payload: true,
											})
										}
									>
										Manage Manufacturers
									</Button>
								</div>

								<Table
									columns={manufacturerColumns}
									dataSource={state.manufacturers}
									loading={state.manufacturersLoading}
									rowKey="id"
									pagination={{
										pageSize: manufacturerPageSize,
										showSizeChanger: true,
										pageSizeOptions: ["5", "10", "20", "50"],
										onShowSizeChange: (current, size) => {
											setManufacturerPageSize(size);
										},
										showQuickJumper: true,
										showTotal: (total, range) =>
											`${range[0]}-${range[1]} of ${total} manufacturers`,
									}}
									scroll={{ x: 600 }}
								/>
							</div>
						</Card>
					</>
				)}
			</div>

			<CustomFieldsModal
				visible={state.showCustomFieldsModal}
				onClose={() =>
					dispatch({ type: "SET_SHOW_CUSTOM_FIELDS_MODAL", payload: false })
				}
				onSuccess={handleCustomFieldsSuccess}
				currentFields={(() => {
					// Find form_id 3 and get its config
					const formId3 = user?.organization?.forms?.find(
						(f: any) => f.formId === 3 || f.form_id === 3,
					);
					// For form_id 3, config is the array directly; otherwise check config.uniqueFields
					let uniqueFields = null;
					if (formId3?.config) {
						if (Array.isArray(formId3.config)) {
							uniqueFields = formId3.config;
						} else if (formId3.config.uniqueFields) {
							uniqueFields = formId3.config.uniqueFields;
						}
					}
					return Array.isArray(uniqueFields)
						? (uniqueFields as CustomField[])
						: null;
				})()}
				organizationId={user?.organization?.id || 0}
				formId={3}
			/>

			<AssetTypeModal
				visible={state.showAssetTypeModal}
				onClose={() =>
					dispatch({ type: "SET_SHOW_ASSET_TYPE_MODAL", payload: false })
				}
				onSuccess={handleAssetTypeSuccess}
				currentAssetTypes={state.assetTypes}
			/>

			<ManufacturerModal
				visible={state.showManufacturerModal}
				onClose={() =>
					dispatch({ type: "SET_SHOW_MANUFACTURER_MODAL", payload: false })
				}
				onSuccess={handleManufacturerSuccess}
				currentManufacturers={state.manufacturers}
			/>

			<EquipmentAreaModal
				visible={state.showEquipmentAreaModal}
				onClose={() =>
					dispatch({ type: "SET_SHOW_EQUIPMENT_AREA_MODAL", payload: false })
				}
				onSuccess={handleEquipmentAreaSuccess}
				currentEquipmentAreas={state.equipmentAreas}
			/>
		</div>
	);
};
